import { store } from '../store';
import { handleUnauthorizedError } from './apiMiddleware';

export interface Supplier {
  id?: string;
  _id?: string;
  name: string;
  country?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: {
    name?: string;
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    countryCode?: string;
    phone?: string;
    email?: string;
  };
  vendorBrands: string[];
  paymentTerms?: string;
  tvaRate?: 0 | 5.5 | 10 | 20;
  paymentDelayDays?: number;
  carrierId?: string; // identifiant du transporteur associé (ex: 'colissimo', 'chronopost')
  createdAt?: Date;
  updatedAt?: Date;
}

// Fonction utilitaire pour récupérer le token d'authentification
const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

// URL de base pour les API des fournisseurs
const API_URL = `${
  import.meta.env.VITE_API_URL || "http://localhost:5000/api"
}/supply/suppliers`;

/**
 * Récupère tous les fournisseurs
 */
export const getAllSuppliers = async (): Promise<Supplier[]> => {
  try {
    const response = await fetch(API_URL, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    // Transformer les réponses pour unifier id et _id
    return data.map((supplier: any) => ({
      ...supplier,
      id: supplier._id || supplier.id,
    }));
  } catch (error) {
    console.error("Erreur lors de la récupération des fournisseurs:", error);
    throw error;
  }
};

/**
 * Récupère un fournisseur par son ID
 * @param id - ID du fournisseur
 */
export const getSupplierById = async (id: string): Promise<Supplier> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return {
      ...data,
      id: data._id || data.id,
    };
  } catch (error) {
    console.error(
      `Erreur lors de la récupération du fournisseur ${id}:`,
      error
    );
    throw error;
  }
};

/**
 * Crée un nouveau fournisseur
 * @param supplier - Données du fournisseur à créer
 */
export const createSupplier = async (supplier: Supplier): Promise<Supplier> => {
  try {
    const response = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader()
      },
      body: JSON.stringify(supplier),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return {
      ...data,
      id: data._id || data.id,
    };
  } catch (error) {
    console.error("Erreur lors de la création du fournisseur:", error);
    throw error;
  }
};

/**
 * Met à jour un fournisseur existant
 * @param id - ID du fournisseur
 * @param supplier - Données mises à jour du fournisseur
 */
export const updateSupplier = async (
  id: string,
  supplier: Partial<Supplier>
): Promise<Supplier> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeader()
      },
      body: JSON.stringify(supplier),
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    const data = await response.json();
    return {
      ...data,
      id: data._id || data.id,
    };
  } catch (error) {
    console.error(`Erreur lors de la mise à jour du fournisseur ${id}:`, error);
    throw error;
  }
};

/**
 * Supprime un fournisseur
 * @param id - ID du fournisseur à supprimer
 */
export const deleteSupplier = async (id: string): Promise<void> => {
  try {
    const response = await fetch(`${API_URL}/${id}`, {
      method: "DELETE",
      headers: {
        ...getAuthHeader()
      }
    });
    if (!response.ok) {
      if (handleUnauthorizedError(response)) return Promise.reject(new Error('Unauthorized'));
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
  } catch (error) {
    console.error(`Erreur lors de la suppression du fournisseur ${id}:`, error);
    throw error;
  }
};
